import { useState } from "react";
import IconComponent from "@CubeComponents/icons";
import { Link } from "@mui/material";
import { useTranslation } from "@i18n";

/**
 * The phone visitor's choice to use the web app anyway. Session-scoped: the nudge towards the CUBE app
 * comes back in a new session, but does not reappear on every navigation in this one (the Router
 * re-mounts this screen on each route change, so component state alone would not survive).
 */
const DISMISSED_KEY = "cube.mobileDeviceRestrict.dismissed";

function readDismissed(): boolean {
  try {
    return globalThis.sessionStorage.getItem(DISMISSED_KEY) === "1";
  } catch {
    // Safari private mode throws on storage access: the screen simply shows until dismissed again.
    return false;
  }
}

function MobileDeviceRestrict() {
  const { t } = useTranslation();
  const [dismissed, setDismissed] = useState(readDismissed);

  const handleDismiss = () => {
    setDismissed(true);
    try {
      globalThis.sessionStorage.setItem(DISMISSED_KEY, "1");
    } catch {
      // Non-fatal: dismissed for this page view only.
    }
  };

  if (dismissed) return null;

  return (
    <>
      <div
        className="flex absolute h-full w-full z-50 flex-col justify-center bg-white text-center  p-6 gap-16"
        data-testid="mobileDeviceRestrict"
      >
        <button
          type="button"
          onClick={handleDismiss}
          aria-label={t("Close")}
          data-testid="mobileDeviceRestrictClose"
          className="absolute top-4 end-4 flex items-center justify-center p-2"
        >
          <IconComponent name="close" width="1.5rem" height="1.5rem" />
        </button>
        <div className="flex flex-col gap-16 items-center justify-center grow">
          <div className="gap-4 flex flex-col items-center justify-center">
            <div className="flex items-center justify-center">
              <IconComponent
                name="DestopViewWarningIllustration"
                className="text-center"
                width="14rem"
                height="14rem"
              />
            </div>
            <div className="text-2xl font-semibold text-text_light text-center mx-7">
              {t("Enjoy a seamless experience with the CUBE app!")}
            </div>
          </div>
          <div className="flex flex-row gap-4 items-center justify-center mx-7">
            <Link href="https://play.google.com/store/apps/details?id=com.prod.cube.ms&hl=en_IN" className="flex-1">
              <IconComponent name="playStoreRedirect" width="10.75rem" height="3.4375rem" />
            </Link>
            <Link href="https://apps.apple.com/us/app/cube-ms/id6504751772" className="flex-1">
              <IconComponent name="appStoreRedirect" width="10.75rem" height="3.375rem" />
            </Link>
          </div>
        </div>
        <div className="text-sm text-text_light">
          {`You can also switch to desktop view by selecting "Request Desktop Website" in browser settings`}
        </div>
      </div>
    </>
  );
}

export default MobileDeviceRestrict;
